import { parseScaled, toSafeNumber } from './decimal.js';
import { CoreError } from './errors.js';

export const SPLIT_METHODS = ['equal', 'exact', 'percentage', 'shares'] as const;
export type SplitMethod = (typeof SPLIT_METHODS)[number];

/** Percentages accept up to 2 decimals (33.33%), stored as basis points. */
export const PERCENT_SCALE = 2;
/** Shares accept up to 2 decimals (1.5 shares). */
export const SHARES_SCALE = 2;
const HUNDRED_PERCENT = 100n * 10n ** BigInt(PERCENT_SCALE);

export type SplitSpec =
  | { method: 'equal'; participants: Array<{ memberId: string }> }
  /** Exact amounts, in minor units of the currency the total is expressed in. */
  | { method: 'exact'; participants: Array<{ memberId: string; amountMinor: number }> }
  | { method: 'percentage'; participants: Array<{ memberId: string; percent: string | number }> }
  | { method: 'shares'; participants: Array<{ memberId: string; shares: string | number }> };

export interface SplitShare {
  memberId: string;
  amountMinor: number;
}

/**
 * Distributes `totalMinor` across integer `weights` so that:
 *  - every part is floor(total * weight / sum) or one unit more,
 *  - the parts always add up to exactly `totalMinor`.
 *
 * Leftover units go to the largest fractional remainders (Hamilton's
 * method); ties go to the earlier participant so results are deterministic.
 */
export function allocateProportionally(totalMinor: number, weights: readonly bigint[]): number[] {
  if (!Number.isSafeInteger(totalMinor) || totalMinor < 0) {
    throw new CoreError('INVALID_AMOUNT', 'Total must be a non-negative whole number of minor units');
  }
  if (weights.length === 0) {
    throw new CoreError('NO_PARTICIPANTS', 'At least one participant is required');
  }
  const weightSum = weights.reduce((sum, w) => {
    if (w <= 0n) throw new CoreError('INVALID_SPLIT_VALUE', 'Split values must be greater than zero');
    return sum + w;
  }, 0n);

  const total = BigInt(totalMinor);
  const parts = weights.map((w) => (total * w) / weightSum);
  const remainders = weights.map((w, index) => ({ index, remainder: (total * w) % weightSum }));
  let leftover = total - parts.reduce((sum, p) => sum + p, 0n);

  remainders.sort((a, b) =>
    a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1,
  );
  for (const { index } of remainders) {
    if (leftover === 0n) break;
    parts[index] = (parts[index] ?? 0n) + 1n;
    leftover -= 1n;
  }
  return parts.map(toSafeNumber);
}

/**
 * Computes how much each participant is responsible for.
 * The returned shares always sum to exactly `totalMinor`.
 */
export function calculateSplit(totalMinor: number, spec: SplitSpec): SplitShare[] {
  const memberIds = spec.participants.map((p) => p.memberId);
  assertParticipants(memberIds);

  const weights = weightsFor(totalMinor, spec);
  const amounts = allocateProportionally(totalMinor, weights);
  return memberIds.map((memberId, i) => ({ memberId, amountMinor: amounts[i] ?? 0 }));
}

function weightsFor(totalMinor: number, spec: SplitSpec): bigint[] {
  switch (spec.method) {
    case 'equal':
      return spec.participants.map(() => 1n);

    case 'exact': {
      const amounts = spec.participants.map(({ amountMinor }) => {
        if (!Number.isSafeInteger(amountMinor) || amountMinor <= 0) {
          throw new CoreError('INVALID_SPLIT_VALUE', 'Each amount must be greater than zero');
        }
        return BigInt(amountMinor);
      });
      const sum = amounts.reduce((a, b) => a + b, 0n);
      if (sum !== BigInt(totalMinor)) {
        throw new CoreError(
          'SPLIT_SUM_MISMATCH',
          `Individual amounts add up to ${sum} but the expense total is ${totalMinor}`,
        );
      }
      return amounts;
    }

    case 'percentage': {
      const basisPoints = spec.participants.map(({ percent }) =>
        parseSplitValue(percent, PERCENT_SCALE),
      );
      const sum = basisPoints.reduce((a, b) => a + b, 0n);
      if (sum !== HUNDRED_PERCENT) {
        throw new CoreError('PERCENTAGE_SUM_INVALID', 'Percentages must add up to exactly 100%');
      }
      return basisPoints;
    }

    case 'shares':
      return spec.participants.map(({ shares }) => parseSplitValue(shares, SHARES_SCALE));

    default: {
      const exhaustive: never = spec;
      throw new CoreError('INVALID_SPLIT_VALUE', `Unknown split method ${String(exhaustive)}`);
    }
  }
}

function parseSplitValue(value: string | number, scale: number): bigint {
  let parsed: bigint;
  try {
    parsed = parseScaled(value, scale);
  } catch {
    throw new CoreError(
      'INVALID_SPLIT_VALUE',
      `"${String(value)}" is not a valid value (max ${scale} decimal places)`,
    );
  }
  if (parsed <= 0n) {
    throw new CoreError('INVALID_SPLIT_VALUE', 'Split values must be greater than zero');
  }
  return parsed;
}

function assertParticipants(memberIds: string[]): void {
  if (memberIds.length === 0) {
    throw new CoreError('NO_PARTICIPANTS', 'At least one participant is required');
  }
  if (new Set(memberIds).size !== memberIds.length) {
    throw new CoreError('DUPLICATE_PARTICIPANT', 'A participant appears more than once');
  }
}
